<template>
  <div class="base-table-container">
    <div v-if="loading" class="base-table__loading">
      <div class="base-table__spinner"></div>
      <p>Loading...</p>
    </div>

    <div v-else-if="!hasData" class="base-table__empty">
      <slot name="empty">
        <div class="base-table__empty-content">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21,15 16,10 5,21"/>
          </svg>
          <h3>{{ emptyTitle || 'No data available' }}</h3>
          <p>{{ emptyMessage || 'There are no items to display at this time.' }}</p>
        </div>
      </slot>
    </div>

    <div v-else class="base-table">
      <table class="base-table__table">
        <thead class="base-table__thead">
          <tr>
            <th
              v-for="column in columns"
              :key="column.key"
              :class="[
                'base-table__th',
                {
                  'base-table__th--sortable': column.sortable,
                  'base-table__th--sorted': sortBy === column.key
                }
              ]"
              @click="handleSort(column)"
            >
              <div class="base-table__th-content">
                <span>{{ column.title || column.key }}</span>
                <span
                  v-if="column.sortable"
                  class="base-table__sort-icon"
                  :class="{
                    'base-table__sort-icon--asc': sortBy === column.key && sortOrder === 'asc',
                    'base-table__sort-icon--desc': sortBy === column.key && sortOrder === 'desc'
                  }"
                >
                  ↕
                </span>
              </div>
            </th>
            <th v-if="$slots.actions" class="base-table__th base-table__th--actions">
              Actions
            </th>
          </tr>
        </thead>

        <tbody class="base-table__tbody">
          <tr
            v-for="(row, index) in paginatedRows"
            :key="getRowKey(row, index)"
            :class="[
              'base-table__tr',
              {
                'base-table__tr--clickable': rowClickable,
                'base-table__tr--striped': striped && index % 2 === 1
              }
            ]"
            @click="handleRowClick(row, index)"
          >
            <td
              v-for="column in columns"
              :key="column.key"
              :class="[
                'base-table__td',
                column.align ? `base-table__td--${column.align}` : ''
              ]"
            >
              <slot
                :name="`cell-${column.key}`"
                :row="row"
                :column="column"
                :value="row[column.key]"
                :index="index"
              >
                {{ formatCellValue(row[column.key], column) }}
              </slot>
            </td>

            <td v-if="$slots.actions" class="base-table__td base-table__td--actions">
              <slot name="actions" :row="row" :index="index" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="showPagination && hasData" class="base-table__pagination">
      <div class="base-table__pagination-info">
        Showing {{ paginationStart }} to {{ paginationEnd }} of {{ totalRows }} results
      </div>

      <div class="base-table__pagination-controls">
        <button
          :disabled="currentPage === 1"
          class="base-table__pagination-btn"
          @click="goToPage(currentPage - 1)"
        >
          Previous
        </button>

        <div class="base-table__pagination-pages">
          <button
            v-for="page in visiblePages"
            :key="page"
            :class="[
              'base-table__pagination-btn',
              {
                'base-table__pagination-btn--active': page === currentPage,
                'base-table__pagination-btn--ellipsis': page === '...'
              }
            ]"
            :disabled="page === '...'"
            @click="goToPage(page)"
          >
            {{ page }}
          </button>
        </div>

        <button
          :disabled="currentPage === totalPages"
          class="base-table__pagination-btn"
          @click="goToPage(currentPage + 1)"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref, watch } from 'vue'

export default defineComponent({
  name: 'BaseTable',
  emits: ['row-click', 'sort', 'page-change'],
  props: {
    columns: {
      type: Array,
      required: true,
      validator: (columns) => {
        return columns.every(col => col.key && typeof col.key === 'string')
      }
    },
    rows: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    striped: {
      type: Boolean,
      default: true
    },
    rowClickable: {
      type: Boolean,
      default: false
    },
    showPagination: {
      type: Boolean,
      default: true
    },
    pageSize: {
      type: Number,
      default: 10
    },
    emptyTitle: {
      type: String,
      default: ''
    },
    emptyMessage: {
      type: String,
      default: ''
    },
    rowKey: {
      type: [String, Function],
      default: 'id'
    }
  },
  setup(props, { emit }) {
    const currentPage = ref(1)
    const sortBy = ref('')
    const sortOrder = ref('asc')

    const hasData = computed(() => {
      return props.rows && props.rows.length > 0
    })

    const totalRows = computed(() => {
      return props.rows?.length || 0
    })

    const totalPages = computed(() => {
      if (!props.showPagination || !hasData.value) return 1
      return Math.ceil(totalRows.value / props.pageSize)
    })

    const sortedRows = computed(() => {
      if (!sortBy.value || !hasData.value) return props.rows

      const column = props.columns.find(col => col.key === sortBy.value)
      if (!column || !column.sortable) return props.rows

      return [...props.rows].sort((a, b) => {
        let aVal = a[sortBy.value]
        let bVal = b[sortBy.value]

        // Handle null/undefined values
        if (aVal == null && bVal == null) return 0
        if (aVal == null) return 1
        if (bVal == null) return -1

        // Custom sort function
        if (column.sortFn) {
          return sortOrder.value === 'asc'
            ? column.sortFn(aVal, bVal)
            : column.sortFn(bVal, aVal)
        }

        // Default sorting
        if (typeof aVal === 'string') aVal = aVal.toLowerCase()
        if (typeof bVal === 'string') bVal = bVal.toLowerCase()

        if (aVal < bVal) return sortOrder.value === 'asc' ? -1 : 1
        if (aVal > bVal) return sortOrder.value === 'asc' ? 1 : -1
        return 0
      })
    })

    const paginatedRows = computed(() => {
      if (!props.showPagination) return sortedRows.value

      const start = (currentPage.value - 1) * props.pageSize
      const end = start + props.pageSize
      return sortedRows.value.slice(start, end)
    })

    const paginationStart = computed(() => {
      if (!hasData.value) return 0
      return (currentPage.value - 1) * props.pageSize + 1
    })

    const paginationEnd = computed(() => {
      if (!hasData.value) return 0
      return Math.min(currentPage.value * props.pageSize, totalRows.value)
    })

    const visiblePages = computed(() => {
      if (totalPages.value <= 7) {
        return Array.from({ length: totalPages.value }, (_, i) => i + 1)
      }

      const pages = []
      const current = currentPage.value

      if (current <= 4) {
        pages.push(1, 2, 3, 4, 5, '...', totalPages.value)
      } else if (current >= totalPages.value - 3) {
        pages.push(
          1, '...',
          totalPages.value - 4,
          totalPages.value - 3,
          totalPages.value - 2,
          totalPages.value - 1,
          totalPages.value
        )
      } else {
        pages.push(
          1, '...',
          current - 1, current, current + 1,
          '...', totalPages.value
        )
      }

      return pages
    })

    const handleSort = (column) => {
      if (!column.sortable) return

      if (sortBy.value === column.key) {
        sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
      } else {
        sortBy.value = column.key
        sortOrder.value = 'asc'
      }

      emit('sort', {
        column: column.key,
        order: sortOrder.value
      })
    }

    const handleRowClick = (row, index) => {
      if (props.rowClickable) {
        emit('row-click', { row, index })
      }
    }

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value && page !== currentPage.value) {
        currentPage.value = page
        emit('page-change', page)
      }
    }

    const getRowKey = (row, index) => {
      if (typeof props.rowKey === 'function') {
        return props.rowKey(row, index)
      }
      return row[props.rowKey] || index
    }

    const formatCellValue = (value, column) => {
      if (column.formatter) {
        return column.formatter(value)
      }

      if (value == null) return ''
      if (typeof value === 'boolean') return value ? 'Yes' : 'No'
      if (value instanceof Date) return value.toLocaleDateString()

      return value
    }

    // Reset to first page when rows change
    watch(() => props.rows, () => {
      currentPage.value = 1
    })

    return {
      currentPage,
      sortBy,
      sortOrder,
      hasData,
      totalRows,
      totalPages,
      paginatedRows,
      paginationStart,
      paginationEnd,
      visiblePages,
      handleSort,
      handleRowClick,
      goToPage,
      getRowKey,
      formatCellValue
    }
  }
})
</script>

<style scoped>
.base-table-container {
  width: 100%;
  background-color: var(--color-white);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-gray-200);
  overflow: hidden;
}

.base-table {
  overflow-x: auto;
}

.base-table__table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
}

/* Table Header */
.base-table__thead {
  background-color: var(--color-gray-50);
}

.base-table__th {
  padding: var(--space-3) var(--space-4);
  text-align: center;
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-700);
  border-bottom: 1px solid var(--color-gray-200);
  white-space: nowrap;
  position: relative;
}

.base-table__th--sortable {
  cursor: pointer;
  user-select: none;
}

.base-table__th--sortable:hover {
  background-color: var(--color-gray-100);
}

.base-table__th--actions {
  width: 1px;
  text-align: center;
}

.base-table__th-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
}

.base-table__sort-icon {
  color: var(--color-gray-400);
  font-size: var(--font-size-xs);
  transition: color var(--transition-fast);
}

.base-table__sort-icon--asc {
  color: var(--color-primary);
}

.base-table__sort-icon--desc {
  color: var(--color-primary);
  transform: rotate(180deg);
}

/* Table Body */
.base-table__tbody {
  background-color: var(--color-white);
}

.base-table__tr {
  transition: background-color var(--transition-fast);
}

.base-table__tr:hover {
  background-color: var(--color-gray-50);
}

.base-table__tr--striped {
  background-color: var(--color-gray-25);
}

.base-table__tr--striped:hover {
  background-color: var(--color-gray-75);
}

.base-table__tr--clickable {
  cursor: pointer;
}

.base-table__tr--clickable:hover {
  background-color: var(--color-primary-light);
  color: var(--color-white);
}

.base-table__td {
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--color-gray-100);
  vertical-align: middle;
  text-align: center;
}

.base-table__td--center {
  text-align: center;
}

.base-table__td--right {
  text-align: right;
}

.base-table__td--actions {
  width: 1px;
  text-align: center;
  white-space: nowrap;
}

/* Loading State */
.base-table__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-12);
  color: var(--color-gray-500);
}

.base-table__spinner {
  width: 2rem;
  height: 2rem;
  border: 2px solid var(--color-gray-200);
  border-top: 2px solid var(--color-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: var(--space-3);
}

/* Empty State */
.base-table__empty {
  padding: var(--space-12);
  text-align: center;
  color: var(--color-gray-500);
}

.base-table__empty-content svg {
  color: var(--color-gray-300);
  margin-bottom: var(--space-4);
}

.base-table__empty-content h3 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-700);
  margin-bottom: var(--space-2);
}

.base-table__empty-content p {
  color: var(--color-gray-500);
  margin: 0;
}

/* Pagination */
.base-table__pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4);
  border-top: 1px solid var(--color-gray-200);
  background-color: var(--color-gray-50);
  font-size: var(--font-size-sm);
}

.base-table__pagination-info {
  color: var(--color-gray-600);
}

.base-table__pagination-controls {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.base-table__pagination-pages {
  display: flex;
  gap: var(--space-1);
}

.base-table__pagination-btn {
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-gray-300);
  background-color: var(--color-white);
  color: var(--color-gray-700);
  border-radius: var(--radius-base);
  cursor: pointer;
  transition: all var(--transition-fast);
  font-size: var(--font-size-sm);
  min-width: 2.5rem;
  text-align: center;
}

.base-table__pagination-btn:hover:not(:disabled) {
  background-color: var(--color-gray-50);
  border-color: var(--color-gray-400);
}

.base-table__pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.base-table__pagination-btn--active {
  background-color: var(--color-primary);
  color: var(--color-white);
  border-color: var(--color-primary);
}

.base-table__pagination-btn--ellipsis {
  border: none;
  background: none;
  cursor: default;
}

/* Responsive adjustments */
@media (max-width: 767px) {
  .base-table__pagination {
    flex-direction: column;
    gap: var(--space-3);
    align-items: stretch;
  }

  .base-table__pagination-controls {
    justify-content: center;
  }

  .base-table__th,
  .base-table__td {
    padding: var(--space-2) var(--space-3);
  }

  .base-table {
    font-size: var(--font-size-xs);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>